/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
  | T
  | {
      [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never;
    };
export interface AdminDashboardInput {
  copilotWindowDays?: number | null | undefined;
  sharedLinkWindowDays?: number | null | undefined;
  storageHistoryDays?: number | null | undefined;
  syncHistoryHours?: number | null | undefined;
  timezone?: string | null | undefined;
}

export interface AdminMailDeliveriesInput {
  hours?: number | null | undefined;
}

export interface AdminUpdateWorkspaceInput {
  avatarKey?: string | null | undefined;
  enableAi?: boolean | null | undefined;
  enableDocEmbedding?: boolean | null | undefined;
  enableSharing?: boolean | null | undefined;
  enableUrlPreview?: boolean | null | undefined;
  id: string;
  name?: string | null | undefined;
  public?: boolean | null | undefined;
}

export enum AdminWorkspaceMemberRole {
  Admin = 'Admin',
  Collaborator = 'Collaborator',
  Owner = 'Owner',
}

export enum AdminWorkspaceSort {
  BlobCount = 'BlobCount',
  BlobSize = 'BlobSize',
  CreatedAt = 'CreatedAt',
  MemberCount = 'MemberCount',
  PublicPageCount = 'PublicPageCount',
  SnapshotCount = 'SnapshotCount',
  SnapshotSize = 'SnapshotSize',
}

export interface AggregateHitsOptions {
  fields: Array<string>;
  highlights?: Array<SearchHighlight> | null | undefined;
  pagination?: AggregateHitsPagination | null | undefined;
}

export interface AggregateHitsPagination {
  limit?: number | null | undefined;
  skip?: number | null | undefined;
}

export interface AggregateInput {
  field: string;
  options: AggregateOptions;
  query: SearchQuery;
  table: SearchTable;
}

export interface AggregateOptions {
  hits: AggregateHitsOptions;
  pagination?: SearchPagination | null | undefined;
}

export enum BlobUploadMethod {
  GRAPHQL = 'GRAPHQL',
  MULTIPART = 'MULTIPART',
  PRESIGNED = 'PRESIGNED',
}

export interface BlobUploadPartInput {
  etag: string;
  partNumber: number;
}

export enum CalendarProviderType {
  Google = 'Google',
}

export interface CreateCheckoutSessionInput {
  args?: unknown;
  coupon?: string | null | undefined;
  /** @deprecated not required anymore */
  idempotencyKey?: string | null | undefined;
  plan?: SubscriptionPlan | null | undefined;
  recurring?: SubscriptionRecurring | null | undefined;
  successCallbackLink: string;
  variant?: SubscriptionVariant | null | undefined;
}

export interface CreateUserInput {
  email: string;
  name?: string | null | undefined;
  password?: string | null | undefined;
}

export enum DocMode {
  edgeless = 'edgeless',
  page = 'page',
}

export enum DocRole {
  Commenter = 'Commenter',
  Editor = 'Editor',
  External = 'External',
  Manager = 'Manager',
  None = 'None',
  Owner = 'Owner',
  Reader = 'Reader',
}

export enum FeatureType {
  Admin = 'Admin',
  FreePlan = 'FreePlan',
  LifetimeProPlan = 'LifetimeProPlan',
  ProPlan = 'ProPlan',
  QuotaExceededReadonlyWorkspace = 'QuotaExceededReadonlyWorkspace',
  TeamPlan = 'TeamPlan',
  UnlimitedCopilot = 'UnlimitedCopilot',
  UnlimitedWorkspace = 'UnlimitedWorkspace',
}

export interface GenerateAccessTokenInput {
  expiresAt?: string | null | undefined;
  name: string;
  workspaceId: string;
}

export interface GrantDocUserRolesInput {
  docId: string;
  role: DocRole;
  userIds: Array<string>;
  workspaceId: string;
}

export interface ImportUsersInput {
  users: Array<CreateUserInput>;
}

export enum InvoiceStatus {
  Draft = 'Draft',
  Open = 'Open',
  Paid = 'Paid',
  Uncollectible = 'Uncollectible',
  Void = 'Void',
}

export interface ListUserInput {
  features?: Array<FeatureType> | null | undefined;
  first?: number | null | undefined;
  keyword?: string | null | undefined;
  skip?: number | null | undefined;
}

export interface ListWorkspaceInput {
  enableAi?: boolean | null | undefined;
  enableDocEmbedding?: boolean | null | undefined;
  enableSharing?: boolean | null | undefined;
  enableUrlPreview?: boolean | null | undefined;
  features?: Array<FeatureType> | null | undefined;
  first?: number;
  keyword?: string | null | undefined;
  orderBy?: AdminWorkspaceSort | null | undefined;
  public?: boolean | null | undefined;
  skip?: number;
}

export interface ManageUserInput {
  /** User email */
  email?: string | null | undefined;
  /** User name */
  name?: string | null | undefined;
}

export interface MentionDocInput {
  blockId?: string | null | undefined;
  elementId?: string | null | undefined;
  id: string;
  mode: DocMode;
  title: string;
}

export interface MentionInput {
  doc: MentionDocInput;
  userId: string;
  workspaceId: string;
}

export enum NotificationLevel {
  Default = 'Default',
  High = 'High',
  Low = 'Low',
  Min = 'Min',
  None = 'None',
}

export enum NotificationType {
  Comment = 'Comment',
  CommentMention = 'CommentMention',
  Invitation = 'Invitation',
  InvitationAccepted = 'InvitationAccepted',
  InvitationBlocked = 'InvitationBlocked',
  InvitationRejected = 'InvitationRejected',
  InvitationReviewApproved = 'InvitationReviewApproved',
  InvitationReviewDeclined = 'InvitationReviewDeclined',
  InvitationReviewRequest = 'InvitationReviewRequest',
  Mention = 'Mention',
}

export enum OAuthProviderType {
  Apple = 'Apple',
  GitHub = 'GitHub',
  Google = 'Google',
  OIDC = 'OIDC',
}

export interface PaginationInput {
  after?: string | null | undefined;
  first?: number | null | undefined;
  offset?: number | null | undefined;
}

export enum Permission {
  Admin = 'Admin',
  Collaborator = 'Collaborator',
  External = 'External',
  Owner = 'Owner',
}

export enum PublicDocMode {
  Edgeless = 'Edgeless',
  Page = 'Page',
}

export interface RevokeDocUserRoleInput {
  docId: string;
  userId: string;
  workspaceId: string;
}

export interface SearchHighlight {
  before: string;
  end: string;
  field: string;
}

export interface SearchInput {
  options: SearchOptions;
  query: SearchQuery;
  table: SearchTable;
}

export interface SearchOptions {
  fields: Array<string>;
  highlights?: Array<SearchHighlight> | null | undefined;
  pagination?: SearchPagination | null | undefined;
}

export interface SearchPagination {
  cursor?: string | null | undefined;
  limit?: number | null | undefined;
  skip?: number | null | undefined;
}

export interface SearchQuery {
  boost?: number | null | undefined;
  field?: string | null | undefined;
  match?: string | null | undefined;
  occur?: SearchQueryOccur | null | undefined;
  queries?: Array<SearchQuery> | null | undefined;
  query?: SearchQuery | null | undefined;
  type: SearchQueryType;
}

export enum SearchQueryOccur {
  must = 'must',
  must_not = 'must_not',
  should = 'should',
}

export enum SearchQueryType {
  all = 'all',
  boolean = 'boolean',
  boost = 'boost',
  exists = 'exists',
  match = 'match',
}

export enum SearchTable {
  block = 'block',
  doc = 'doc',
}

export enum ServerDeploymentType {
  Selfhosted = 'Selfhosted',
  Xuwo = 'Xuwo',
}

export enum ServerFeature {
  Captcha = 'Captcha',
  Comment = 'Comment',
  Copilot = 'Copilot',
  CopilotEmbedding = 'CopilotEmbedding',
  Indexer = 'Indexer',
  LocalWorkspace = 'LocalWorkspace',
  OAuth = 'OAuth',
  Payment = 'Payment',
}

export enum SubscriptionPlan {
  AI = 'AI',
  Enterprise = 'Enterprise',
  Free = 'Free',
  Pro = 'Pro',
  SelfHosted = 'SelfHosted',
  SelfHostedTeam = 'SelfHostedTeam',
  Team = 'Team',
}

export enum SubscriptionRecurring {
  Lifetime = 'Lifetime',
  Monthly = 'Monthly',
  Yearly = 'Yearly',
}

export enum SubscriptionStatus {
  Active = 'Active',
  Canceled = 'Canceled',
  Incomplete = 'Incomplete',
  IncompleteExpired = 'IncompleteExpired',
  PastDue = 'PastDue',
  Paused = 'Paused',
  Trialing = 'Trialing',
  Unpaid = 'Unpaid',
}

export enum SubscriptionVariant {
  Onetime = 'Onetime',
}

/** 客户端 TimeBucket 枚举(生成 schema:Day/Hour/Minute) */
export enum TimeBucket {
  Day = 'Day',
  Hour = 'Hour',
  Minute = 'Minute',
}

export interface UpdateAppConfigInput {
  key: string;
  module: string;
  value: Record<string, string>;
}

export interface UpdateDocDefaultRoleInput {
  docId: string;
  role: DocRole;
  workspaceId: string;
}

export interface UpdateDocUserRoleInput {
  docId: string;
  role: DocRole;
  userId: string;
  workspaceId: string;
}

export interface UpdateUserInput {
  name?: string | null | undefined;
}

export enum WorkspaceInviteLinkExpireTime {
  OneDay = 'OneDay',
  OneMonth = 'OneMonth',
  OneWeek = 'OneWeek',
  ThreeDays = 'ThreeDays',
}

export enum WorkspaceMemberStatus {
  Accepted = 'Accepted',
  AllocatingSeat = 'AllocatingSeat',
  NeedMoreSeat = 'NeedMoreSeat',
  NeedMoreSeatAndReview = 'NeedMoreSeatAndReview',
  Pending = 'Pending',
  UnderReview = 'UnderReview',
}

export type GenerateUserAccessTokenMutationVariables = Exact<{
  input: GenerateAccessTokenInput;
}>;

export interface GenerateUserAccessTokenMutation {
  generateUserAccessToken: {
    id: string;
    name: string;
    token: string;
    createdAt: string;
    expiresAt: string | null;
  };
}

export type RevokeUserAccessTokenMutationVariables = Exact<{
  id: string;
}>;

export interface RevokeUserAccessTokenMutation {
  revokeUserAccessToken: boolean;
}

export type AdminDashboardQueryVariables = Exact<{
  input?: AdminDashboardInput | null | undefined;
}>;

export interface AdminDashboardQuery {
  adminDashboard: {
    syncActiveUsers: number;
    copilotConversations: number | null;
    workspaceStorageBytes: number;
    blobStorageBytes: number;
    generatedAt: string;
    syncActiveUsersTimeline: Array<{ minute: string; activeUsers: number }>;
    syncWindow: {
      from: string;
      to: string;
      timezone: string;
      bucket: TimeBucket;
      requestedSize: number;
      effectiveSize: number;
    };
    copilotWindow: {
      from: string;
      to: string;
      timezone: string;
      bucket: TimeBucket;
      requestedSize: number;
      effectiveSize: number;
    };
    workspaceStorageHistory: Array<{ date: string; value: number }>;
    blobStorageHistory: Array<{ date: string; value: number }>;
    storageWindow: {
      from: string;
      to: string;
      timezone: string;
      bucket: TimeBucket;
      requestedSize: number;
      effectiveSize: number;
    };
    topSharedLinks: Array<{
      workspaceId: string;
      docId: string;
      title: string | null;
      shareUrl: string;
      publishedAt: string | null;
      views: number;
      uniqueViews: number;
      guestViews: number;
      lastAccessedAt: string | null;
    }>;
    topSharedLinksWindow: {
      from: string;
      to: string;
      timezone: string;
      bucket: TimeBucket;
      requestedSize: number;
      effectiveSize: number;
    };
  };
}

export type AdminMailDeliveriesQueryVariables = Exact<{
  input?: AdminMailDeliveriesInput | null | undefined;
}>;

export interface AdminMailDeliveriesQuery {
  adminMailDeliveries: {
    window: {
      from: string;
      to: string;
      timezone: string;
      bucket: TimeBucket;
      requestedSize: number;
      effectiveSize: number;
    };
    summary: {
      total: number;
      sent: number;
      failed: number;
      skipped: number;
      canceled: number;
      queued: number;
      sending: number;
      retryWait: number;
      successRate: number;
    };
    byStatus: Array<{
      key: string;
      label: string;
      total: number;
      points: Array<{ bucket: string; count: number }>;
    }>;
    byType: Array<{
      key: string;
      label: string;
      total: number;
      points: Array<{ bucket: string; count: number }>;
    }>;
    byOutcome: Array<{
      key: string;
      label: string;
      total: number;
      points: Array<{ bucket: string; count: number }>;
    }>;
  };
}

export type AdminServerConfigQueryVariables = Exact<{ [key: string]: never }>;

export interface AdminServerConfigQuery {
  serverConfig: {
    version: string;
    baseUrl: string;
    name: string;
    features: Array<ServerFeature>;
    type: ServerDeploymentType;
    initialized: boolean;
    availableUserFeatures: Array<FeatureType>;
    availableWorkspaceFeatures: Array<FeatureType>;
    credentialsRequirement: {
      password: { minLength: number; maxLength: number };
    };
    availableUpgrade: {
      changelog: string;
      version: string;
      publishedAt: string;
      url: string;
    } | null;
  };
}

export type AdminUpdateWorkspaceMutationVariables = Exact<{
  input: AdminUpdateWorkspaceInput;
}>;

export interface AdminUpdateWorkspaceMutation {
  adminUpdateWorkspace: {
    id: string;
    public: boolean;
    createdAt: string;
    name: string | null;
    avatarKey: string | null;
    enableAi: boolean;
    enableSharing: boolean;
    enableUrlPreview: boolean;
    enableDocEmbedding: boolean;
    features: Array<FeatureType>;
    memberCount: number;
    publicPageCount: number;
    snapshotCount: number;
    snapshotSize: number;
    blobCount: number;
    blobSize: number;
    owner: {
      id: string;
      name: string;
      email: string;
      avatarUrl: string | null;
    } | null;
  } | null;
}

export type AdminWorkspaceQueryVariables = Exact<{
  id: string;
  memberSkip?: number | null | undefined;
  memberTake?: number | null | undefined;
  memberQuery?: string | null | undefined;
}>;

export interface AdminWorkspaceQuery {
  adminWorkspace: {
    id: string;
    public: boolean;
    createdAt: string;
    name: string | null;
    avatarKey: string | null;
    enableAi: boolean;
    enableSharing: boolean;
    enableUrlPreview: boolean;
    enableDocEmbedding: boolean;
    features: Array<FeatureType>;
    memberCount: number;
    publicPageCount: number;
    snapshotCount: number;
    snapshotSize: number;
    blobCount: number;
    blobSize: number;
    owner: {
      id: string;
      name: string;
      email: string;
      avatarUrl: string | null;
    } | null;
    sharedLinks: Array<{
      docId: string;
      title: string | null;
      publishedAt: string | null;
    }>;
    members: Array<{
      id: string;
      name: string;
      email: string;
      avatarUrl: string | null;
      role: AdminWorkspaceMemberRole;
      status: WorkspaceMemberStatus;
    }>;
  } | null;
}

export type AdminWorkspacesQueryVariables = Exact<{
  filter: ListWorkspaceInput;
}>;

export interface AdminWorkspacesQuery {
  adminWorkspaces: Array<{
    id: string;
    public: boolean;
    createdAt: string;
    name: string | null;
    avatarKey: string | null;
    enableAi: boolean;
    enableSharing: boolean;
    enableUrlPreview: boolean;
    enableDocEmbedding: boolean;
    features: Array<FeatureType>;
    memberCount: number;
    publicPageCount: number;
    snapshotCount: number;
    snapshotSize: number;
    blobCount: number;
    blobSize: number;
    owner: {
      id: string;
      name: string;
      email: string;
      avatarUrl: string | null;
    } | null;
  }>;
}

export type AdminWorkspacesCountQueryVariables = Exact<{
  filter: ListWorkspaceInput;
}>;

export interface AdminWorkspacesCountQuery {
  adminWorkspacesCount: number;
}

export type AuthSigningKeysQueryVariables = Exact<{ [key: string]: never }>;

export interface AuthSigningKeysQuery {
  authSigningKeys: Array<{
    id: string;
    status: string;
    source: string;
    createdAt: string | null;
    retiredAt: string | null;
    verifyUntil: string | null;
    canDelete: boolean;
  }>;
}

export type CreateChangePasswordUrlMutationVariables = Exact<{
  callbackUrl: string;
  userId: string;
}>;

export interface CreateChangePasswordUrlMutation {
  createChangePasswordUrl: string;
}

export type AppConfigQueryVariables = Exact<{ [key: string]: never }>;

export interface AppConfigQuery {
  appConfig: unknown;
}

export type CreateUserMutationVariables = Exact<{
  input: CreateUserInput;
}>;

export interface CreateUserMutation {
  createUser: { id: string };
}

export type DeleteAuthSigningKeyMutationVariables = Exact<{
  id: string;
}>;

export interface DeleteAuthSigningKeyMutation {
  deleteAuthSigningKey: Array<{
    id: string;
    status: string;
    source: string;
    createdAt: string | null;
    retiredAt: string | null;
    verifyUntil: string | null;
    canDelete: boolean;
  }>;
}

export type DeleteUserMutationVariables = Exact<{
  id: string;
}>;

export interface DeleteUserMutation {
  deleteUser: { success: boolean };
}

export type DisableUserMutationVariables = Exact<{
  id: string;
}>;

export interface DisableUserMutation {
  banUser: { email: string; disabled: boolean };
}

export type EnableUserMutationVariables = Exact<{
  id: string;
}>;

export interface EnableUserMutation {
  enableUser: { email: string; disabled: boolean };
}

export type ImportUsersMutationVariables = Exact<{
  input: ImportUsersInput;
}>;

export interface ImportUsersMutation {
  importUsers: Array<
    | { __typename: 'UserImportFailedType'; email: string; error: string }
    | { __typename: 'UserType'; id: string; name: string; email: string }
  >;
}

export type ListUsersQueryVariables = Exact<{
  filter: ListUserInput;
}>;

export interface ListUsersQuery {
  usersCount: number;
  users: Array<{
    id: string;
    name: string;
    email: string;
    disabled: boolean;
    features: Array<FeatureType>;
    hasPassword: boolean | null;
    emailVerified: boolean;
    avatarUrl: string | null;
  }>;
}

export type RotateAuthSigningKeyMutationVariables = Exact<{
  expectedActiveKeyId: string;
}>;

export interface RotateAuthSigningKeyMutation {
  rotateAuthSigningKey: Array<{
    id: string;
    status: string;
    source: string;
    createdAt: string | null;
    retiredAt: string | null;
    verifyUntil: string | null;
    canDelete: boolean;
  }>;
}

export type SendTestEmailMutationVariables = Exact<{
  name: string;
  host: string;
  port: number;
  sender: string;
  username: string;
  password: string;
  ignoreTLS: boolean;
}>;

export interface SendTestEmailMutation {
  sendTestEmail: boolean;
}

export type UpdateAccountFeaturesMutationVariables = Exact<{
  userId: string;
  features: Array<FeatureType> | FeatureType;
}>;

export interface UpdateAccountFeaturesMutation {
  updateUserFeatures: Array<FeatureType>;
}

export type UpdateAccountMutationVariables = Exact<{
  id: string;
  input: ManageUserInput;
}>;

export interface UpdateAccountMutation {
  updateUser: { id: string; name: string; email: string };
}

export type UpdateAppConfigMutationVariables = Exact<{
  updates: Array<UpdateAppConfigInput> | UpdateAppConfigInput;
}>;

export interface UpdateAppConfigMutation {
  updateAppConfig: unknown;
}

export type DeleteBlobMutationVariables = Exact<{
  workspaceId: string;
  key: string;
  permanently?: boolean | null | undefined;
}>;

export interface DeleteBlobMutation {
  deleteBlob: boolean;
}

export type ListBlobsQueryVariables = Exact<{
  workspaceId: string;
}>;

export interface ListBlobsQuery {
  workspace: {
    blobs: Array<{
      key: string;
      size: number;
      mime: string;
      createdAt: string;
    }>;
  };
}

export type ReleaseDeletedBlobsMutationVariables = Exact<{
  workspaceId: string;
}>;

export interface ReleaseDeletedBlobsMutation {
  releaseDeletedBlobs: boolean;
}

export type SetBlobMutationVariables = Exact<{
  workspaceId: string;
  blob: File;
}>;

export interface SetBlobMutation {
  setBlob: string;
}

export type AbortBlobUploadMutationVariables = Exact<{
  workspaceId: string;
  key: string;
  uploadId: string;
}>;

export interface AbortBlobUploadMutation {
  abortBlobUpload: boolean;
}

export type CompleteBlobUploadMutationVariables = Exact<{
  workspaceId: string;
  key: string;
  uploadId?: string | null | undefined;
  parts?: Array<BlobUploadPartInput> | BlobUploadPartInput | null | undefined;
}>;

export interface CompleteBlobUploadMutation {
  completeBlobUpload: boolean;
}

export type CreateBlobUploadMutationVariables = Exact<{
  workspaceId: string;
  key: string;
  size: number;
  mime: string;
}>;

export interface CreateBlobUploadMutation {
  createBlobUpload: {
    method: BlobUploadMethod;
    blobKey: string;
    alreadyUploaded: boolean;
    uploadUrl: string | null;
    headers: unknown;
    expiresAt: string | null;
    uploadId: string | null;
    partSize: number | null;
    uploadedParts: Array<{ partNumber: number; etag: string }> | null;
  };
}

export type GetBlobUploadPartUrlQueryVariables = Exact<{
  workspaceId: string;
  key: string;
  uploadId: string;
  partNumber: number;
}>;

export interface GetBlobUploadPartUrlQuery {
  workspace: {
    blobUploadPartUrl: {
      uploadUrl: string;
      headers: unknown;
      expiresAt: string | null;
    };
  };
}

export type CancelSubscriptionMutationVariables = Exact<{
  plan?: SubscriptionPlan | null | undefined;
  workspaceId?: string | null | undefined;
}>;

export interface CancelSubscriptionMutation {
  cancelSubscription: {
    id: string | null;
    status: SubscriptionStatus;
    nextBillAt: string | null;
    canceledAt: string | null;
  };
}

export type ChangeEmailMutationVariables = Exact<{
  token: string;
  email: string;
}>;

export interface ChangeEmailMutation {
  changeEmail: { id: string; email: string };
}

export type ChangePasswordMutationVariables = Exact<{
  token: string;
  userId: string;
  newPassword: string;
}>;

export interface ChangePasswordMutation {
  changePassword: boolean;
}

export type CopilotQuotaQueryVariables = Exact<{ [key: string]: never }>;

export interface CopilotQuotaQuery {
  currentUser: {
    copilot: { quota: { limit: number | null; used: number } };
  } | null;
}

export type CreateCheckoutSessionMutationVariables = Exact<{
  input: CreateCheckoutSessionInput;
}>;

export interface CreateCheckoutSessionMutation {
  createCheckoutSession: string;
}

export type CreateWorkspaceMutationVariables = Exact<{
  id?: string | null | undefined;
  rootDoc?: string | null | undefined;
}>;

export interface CreateWorkspaceMutation {
  createWorkspace: { id: string; public: boolean; createdAt: string };
}

export type DeleteAccountMutationVariables = Exact<{ [key: string]: never }>;

export interface DeleteAccountMutation {
  deleteAccount: { success: boolean };
}

export type DeleteWorkspaceMutationVariables = Exact<{
  id: string;
}>;

export interface DeleteWorkspaceMutation {
  deleteWorkspace: boolean;
}

export type GetDocRolePermissionsQueryVariables = Exact<{
  workspaceId: string;
  docId: string;
}>;

export interface GetDocRolePermissionsQuery {
  workspace: {
    doc: {
      permissions: {
        Doc_Copy: boolean;
        Doc_Delete: boolean;
        Doc_Duplicate: boolean;
        Doc_Properties_Read: boolean;
        Doc_Properties_Update: boolean;
        Doc_Publish: boolean;
        Doc_Read: boolean;
        Doc_Restore: boolean;
        Doc_TransferOwner: boolean;
        Doc_Trash: boolean;
        Doc_Update: boolean;
        Doc_Users_Manage: boolean;
        Doc_Users_Read: boolean;
        Doc_Comments_Create: boolean;
        Doc_Comments_Delete: boolean;
        Doc_Comments_Read: boolean;
        Doc_Comments_Resolve: boolean;
      };
    };
  };
}

export type CredentialsRequirementsFragment = {
  password: { minLength: number; maxLength: number };
};

export type PasswordLimitsFragment = { minLength: number; maxLength: number };

export type GetCurrentUserFeaturesQueryVariables = Exact<{
  [key: string]: never;
}>;

export interface GetCurrentUserFeaturesQuery {
  currentUser: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    avatarUrl: string | null;
    features: Array<FeatureType>;
  } | null;
}

export type GetDocCreatedByUpdatedByListQueryVariables = Exact<{
  workspaceId: string;
  pagination: PaginationInput;
}>;

export interface GetDocCreatedByUpdatedByListQuery {
  workspace: {
    docs: {
      totalCount: number;
      pageInfo: { endCursor: string | null; hasNextPage: boolean };
      edges: Array<{
        node: {
          id: string;
          creatorId: string | null;
          lastUpdaterId: string | null;
        };
      }>;
    };
  };
}

export type GetDocSummaryQueryVariables = Exact<{
  workspaceId: string;
  docId: string;
}>;

export interface GetDocSummaryQuery {
  workspace: { doc: { summary: string | null } };
}

export type GetInviteInfoQueryVariables = Exact<{
  inviteId: string;
}>;

export interface GetInviteInfoQuery {
  getInviteInfo: {
    status: WorkspaceMemberStatus | null;
    workspace: { id: string; name: string; avatar: string };
    user: { id: string; name: string; avatarUrl: string | null };
    invitee: {
      id: string;
      name: string;
      email: string;
      avatarUrl: string | null;
    };
  };
}

export type OauthProvidersQueryVariables = Exact<{ [key: string]: never }>;

export interface OauthProvidersQuery {
  serverConfig: { oauthProviders: Array<OAuthProviderType> };
}

export type GetPublicUserByIdQueryVariables = Exact<{
  id: string;
}>;

export interface GetPublicUserByIdQuery {
  publicUserById: { id: string; avatarUrl: string | null; name: string } | null;
}

export type GetUserFeaturesQueryVariables = Exact<{ [key: string]: never }>;

export interface GetUserFeaturesQuery {
  currentUser: { id: string; features: Array<FeatureType> } | null;
}

export type GetWorkspaceAccessQueryVariables = Exact<{
  id: string;
}>;

export interface GetWorkspaceAccessQuery {
  workspace: {
    id: string;
    team: boolean;
    permissions: {
      Workspace_Delete: boolean;
      Workspace_Settings_Update: boolean;
    };
  };
}

export type GetWorkspacePageMetaByIdQueryVariables = Exact<{
  id: string;
  pageId: string;
}>;

export interface GetWorkspacePageMetaByIdQuery {
  workspace: {
    pageMeta: {
      createdAt: string;
      updatedAt: string;
      createdBy: { name: string; avatarUrl: string | null } | null;
      updatedBy: { name: string; avatarUrl: string | null } | null;
    };
  };
}

export type GetWorkspacePublicPagesQueryVariables = Exact<{
  workspaceId: string;
}>;

export interface GetWorkspacePublicPagesQuery {
  workspace: { publicDocs: Array<{ id: string; mode: PublicDocMode }> };
}

export type GetWorkspaceSubscriptionQueryVariables = Exact<{
  workspaceId: string;
}>;

export interface GetWorkspaceSubscriptionQuery {
  workspace: {
    subscription: {
      id: string | null;
      status: SubscriptionStatus;
      plan: SubscriptionPlan;
      recurring: SubscriptionRecurring;
      start: string;
      end: string | null;
      nextBillAt: string | null;
      canceledAt: string | null;
      variant: SubscriptionVariant | null;
    } | null;
  };
}

export type GetWorkspacesQueryVariables = Exact<{ [key: string]: never }>;

export interface GetWorkspacesQuery {
  workspaces: Array<{
    id: string;
    initialized: boolean;
    team: boolean;
    owner: { id: string };
  }>;
}

export type GrantDocUserRolesMutationVariables = Exact<{
  input: GrantDocUserRolesInput;
}>;

export interface GrantDocUserRolesMutation {
  grantDocUserRoles: boolean;
}

export type ListHistoryQueryVariables = Exact<{
  workspaceId: string;
  pageDocId: string;
  take?: number | null | undefined;
  before?: string | null | undefined;
}>;

export interface ListHistoryQuery {
  workspace: {
    historyCurrentRevisionId: string | null;
    histories: Array<{
      id: string;
      revisionId: string | null;
      timestamp: string;
      recordedAt: string;
      actor: string | null;
      isCurrent: boolean | null;
      editor: { name: string; avatarUrl: string | null } | null;
    }>;
  };
}

export type IndexerAggregateQueryVariables = Exact<{
  id: string;
  input: AggregateInput;
}>;

export interface IndexerAggregateQuery {
  workspace: {
    aggregate: {
      buckets: Array<{
        key: string;
        count: number;
        hits: { nodes: Array<{ fields: unknown; highlights: unknown }> };
      }>;
      pagination: {
        count: number;
        hasMore: boolean;
        nextCursor: string | null;
      };
    };
  };
}

export type IndexerSearchQueryVariables = Exact<{
  id: string;
  input: SearchInput;
}>;

export interface IndexerSearchQuery {
  workspace: {
    search: {
      nodes: Array<{ fields: unknown; highlights: unknown }>;
      pagination: {
        count: number;
        hasMore: boolean;
        nextCursor: string | null;
      };
    };
  };
}

export type InvoicesQueryVariables = Exact<{
  take: number;
  skip: number;
}>;

export interface InvoicesQuery {
  currentUser: {
    invoiceCount: number;
    invoices: Array<{
      status: InvoiceStatus;
      currency: string;
      amount: number;
      reason: string;
      lastPaymentError: string | null;
      link: string | null;
      createdAt: string;
    }>;
  } | null;
}

export type LeaveWorkspaceMutationVariables = Exact<{
  workspaceId: string;
  sendLeaveMail?: boolean | null | undefined;
}>;

export interface LeaveWorkspaceMutation {
  leaveWorkspace: boolean;
}

export type ListNotificationsQueryVariables = Exact<{
  pagination: PaginationInput;
}>;

export interface ListNotificationsQuery {
  currentUser: {
    notifications: {
      totalCount: number;
      edges: Array<{
        cursor: string;
        node: {
          id: string;
          type: NotificationType;
          level: NotificationLevel;
          read: boolean;
          createdAt: string;
          updatedAt: string;
          body: unknown;
        };
      }>;
      pageInfo: {
        startCursor: string | null;
        endCursor: string | null;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
    };
  } | null;
}

export type MentionUserMutationVariables = Exact<{
  input: MentionInput;
}>;

export interface MentionUserMutation {
  mentionUser: string;
}

export type PricesQueryVariables = Exact<{ [key: string]: never }>;

export interface PricesQuery {
  prices: Array<{
    type: string;
    plan: SubscriptionPlan;
    currency: string;
    amount: number | null;
    yearlyAmount: number | null;
    lifetimeAmount: number | null;
  }>;
}

export type PublishPageMutationVariables = Exact<{
  workspaceId: string;
  pageId: string;
  mode?: PublicDocMode | null | undefined;
}>;

export interface PublishPageMutation {
  publishDoc: { id: string; mode: PublicDocMode };
}

export type ReadAllNotificationsMutationVariables = Exact<{
  [key: string]: never;
}>;

export interface ReadAllNotificationsMutation {
  readAllNotifications: boolean;
}

export type ReadNotificationMutationVariables = Exact<{
  id: string;
}>;

export interface ReadNotificationMutation {
  readNotification: boolean;
}

export type RecoverDocMutationVariables = Exact<{
  workspaceId: string;
  docId: string;
  timestamp: string;
}>;

export interface RecoverDocMutation {
  recoverDoc: string;
}

export type RemoveAvatarMutationVariables = Exact<{ [key: string]: never }>;

export interface RemoveAvatarMutation {
  removeAvatar: { success: boolean };
}

export type ResumeSubscriptionMutationVariables = Exact<{
  plan?: SubscriptionPlan | null | undefined;
  workspaceId?: string | null | undefined;
}>;

export interface ResumeSubscriptionMutation {
  resumeSubscription: {
    id: string | null;
    status: SubscriptionStatus;
    nextBillAt: string | null;
    start: string;
    end: string | null;
  };
}

export type RevokeDocUserRolesMutationVariables = Exact<{
  input: RevokeDocUserRoleInput;
}>;

export interface RevokeDocUserRolesMutation {
  revokeDocUserRoles: boolean;
}

export type RevokeMemberPermissionMutationVariables = Exact<{
  workspaceId: string;
  userId: string;
}>;

export interface RevokeMemberPermissionMutation {
  revokeMember: boolean;
}

export type RevokePublicPageMutationVariables = Exact<{
  workspaceId: string;
  pageId: string;
}>;

export interface RevokePublicPageMutation {
  revokePublicDoc: { id: string; mode: PublicDocMode; public: boolean };
}

export type SendChangeEmailMutationVariables = Exact<{
  callbackUrl: string;
}>;

export interface SendChangeEmailMutation {
  sendChangeEmail: boolean;
}

export type SendChangePasswordEmailMutationVariables = Exact<{
  callbackUrl: string;
}>;

export interface SendChangePasswordEmailMutation {
  sendChangePasswordEmail: boolean;
}

export type SendSetPasswordEmailMutationVariables = Exact<{
  callbackUrl: string;
}>;

export interface SendSetPasswordEmailMutation {
  sendSetPasswordEmail: boolean;
}

export type SendVerifyChangeEmailMutationVariables = Exact<{
  token: string;
  email: string;
  callbackUrl: string;
}>;

export interface SendVerifyChangeEmailMutation {
  sendVerifyChangeEmail: boolean;
}

export type SendVerifyEmailMutationVariables = Exact<{
  callbackUrl: string;
}>;

export interface SendVerifyEmailMutation {
  sendVerifyEmail: boolean;
}

export type ServerConfigQueryVariables = Exact<{ [key: string]: never }>;

export interface ServerConfigQuery {
  serverConfig: {
    version: string;
    baseUrl: string;
    name: string;
    features: Array<ServerFeature>;
    type: ServerDeploymentType;
    initialized: boolean;
    calendarProviders: Array<CalendarProviderType>;
    credentialsRequirement: {
      password: { minLength: number; maxLength: number };
    };
  };
}

export type SubscriptionQueryVariables = Exact<{ [key: string]: never }>;

export interface SubscriptionQuery {
  currentUser: {
    id: string;
    subscriptions: Array<{
      id: string | null;
      status: SubscriptionStatus;
      plan: SubscriptionPlan;
      recurring: SubscriptionRecurring;
      start: string;
      end: string | null;
      nextBillAt: string | null;
      canceledAt: string | null;
      variant: SubscriptionVariant | null;
    }>;
  } | null;
}

export type UpdateDocDefaultRoleMutationVariables = Exact<{
  input: UpdateDocDefaultRoleInput;
}>;

export interface UpdateDocDefaultRoleMutation {
  updateDocDefaultRole: boolean;
}

export type UpdateDocUserRoleMutationVariables = Exact<{
  input: UpdateDocUserRoleInput;
}>;

export interface UpdateDocUserRoleMutation {
  updateDocUserRole: boolean;
}

export type UpdateSubscriptionMutationVariables = Exact<{
  plan?: SubscriptionPlan | null | undefined;
  recurring: SubscriptionRecurring;
  workspaceId?: string | null | undefined;
}>;

export interface UpdateSubscriptionMutation {
  updateSubscriptionRecurring: {
    id: string | null;
    plan: SubscriptionPlan;
    recurring: SubscriptionRecurring;
    nextBillAt: string | null;
  };
}

export type UpdateUserProfileMutationVariables = Exact<{
  input: UpdateUserInput;
}>;

export interface UpdateUserProfileMutation {
  updateProfile: { id: string; name: string };
}

export type UploadAvatarMutationVariables = Exact<{
  avatar: File;
}>;

export interface UploadAvatarMutation {
  uploadAvatar: {
    id: string;
    name: string;
    avatarUrl: string | null;
    email: string;
  };
}

export type VerifyEmailMutationVariables = Exact<{
  token: string;
}>;

export interface VerifyEmailMutation {
  verifyEmail: boolean;
}

export type WorkspaceBlobQuotaQueryVariables = Exact<{
  id: string;
}>;

export interface WorkspaceBlobQuotaQuery {
  workspace: {
    quota: { blobLimit: number; humanReadable: { blobLimit: string } };
  };
}

export type SetEnableSharingMutationVariables = Exact<{
  id: string;
  enableSharing?: boolean | null | undefined;
  shareAiSessions?: boolean | null | undefined;
}>;

export interface SetEnableSharingMutation {
  updateWorkspace: { id: string };
}

export type InviteByEmailsMutationVariables = Exact<{
  workspaceId: string;
  emails: Array<string> | string;
}>;

export interface InviteByEmailsMutation {
  inviteMembers: Array<{ email: string; inviteId: string | null }>;
}

export type AcceptInviteByInviteIdMutationVariables = Exact<{
  workspaceId: string;
  inviteId: string;
}>;

export interface AcceptInviteByInviteIdMutation {
  acceptInviteById: boolean;
}

export type CreateInviteLinkMutationVariables = Exact<{
  workspaceId: string;
  expireTime: WorkspaceInviteLinkExpireTime;
}>;

export interface CreateInviteLinkMutation {
  createInviteLink: { link: string; expireTime: string };
}

export type RevokeInviteLinkMutationVariables = Exact<{
  workspaceId: string;
}>;

export interface RevokeInviteLinkMutation {
  revokeInviteLink: boolean;
}

export type WorkspaceInvoicesQueryVariables = Exact<{
  take: number;
  skip: number;
  workspaceId: string;
}>;

export interface WorkspaceInvoicesQuery {
  workspace: {
    invoiceCount: number;
    invoices: Array<{
      status: InvoiceStatus;
      currency: string;
      amount: number;
      reason: string;
      lastPaymentError: string | null;
      link: string | null;
      createdAt: string;
    }>;
  };
}

export type ApproveWorkspaceTeamMemberMutationVariables = Exact<{
  workspaceId: string;
  userId: string;
}>;

export interface ApproveWorkspaceTeamMemberMutation {
  approveMember: boolean;
}

export type GrantWorkspaceTeamMemberMutationVariables = Exact<{
  workspaceId: string;
  userId: string;
  permission: Permission;
}>;

export interface GrantWorkspaceTeamMemberMutation {
  grantMember: boolean;
}

export type Queries =
  | {
      name: 'adminDashboardQuery';
      variables: AdminDashboardQueryVariables;
      response: AdminDashboardQuery;
    }
  | {
      name: 'adminMailDeliveriesQuery';
      variables: AdminMailDeliveriesQueryVariables;
      response: AdminMailDeliveriesQuery;
    }
  | {
      name: 'adminServerConfigQuery';
      variables: AdminServerConfigQueryVariables;
      response: AdminServerConfigQuery;
    }
  | {
      name: 'adminWorkspaceQuery';
      variables: AdminWorkspaceQueryVariables;
      response: AdminWorkspaceQuery;
    }
  | {
      name: 'adminWorkspacesQuery';
      variables: AdminWorkspacesQueryVariables;
      response: AdminWorkspacesQuery;
    }
  | {
      name: 'adminWorkspacesCountQuery';
      variables: AdminWorkspacesCountQueryVariables;
      response: AdminWorkspacesCountQuery;
    }
  | {
      name: 'authSigningKeysQuery';
      variables: AuthSigningKeysQueryVariables;
      response: AuthSigningKeysQuery;
    }
  | {
      name: 'appConfigQuery';
      variables: AppConfigQueryVariables;
      response: AppConfigQuery;
    }
  | {
      name: 'listUsersQuery';
      variables: ListUsersQueryVariables;
      response: ListUsersQuery;
    }
  | {
      name: 'listBlobsQuery';
      variables: ListBlobsQueryVariables;
      response: ListBlobsQuery;
    }
  | {
      name: 'getBlobUploadPartUrlQuery';
      variables: GetBlobUploadPartUrlQueryVariables;
      response: GetBlobUploadPartUrlQuery;
    }
  | {
      name: 'copilotQuotaQuery';
      variables: CopilotQuotaQueryVariables;
      response: CopilotQuotaQuery;
    }
  | {
      name: 'getDocRolePermissionsQuery';
      variables: GetDocRolePermissionsQueryVariables;
      response: GetDocRolePermissionsQuery;
    }
  | {
      name: 'getCurrentUserFeaturesQuery';
      variables: GetCurrentUserFeaturesQueryVariables;
      response: GetCurrentUserFeaturesQuery;
    }
  | {
      name: 'getDocCreatedByUpdatedByListQuery';
      variables: GetDocCreatedByUpdatedByListQueryVariables;
      response: GetDocCreatedByUpdatedByListQuery;
    }
  | {
      name: 'getDocSummaryQuery';
      variables: GetDocSummaryQueryVariables;
      response: GetDocSummaryQuery;
    }
  | {
      name: 'getInviteInfoQuery';
      variables: GetInviteInfoQueryVariables;
      response: GetInviteInfoQuery;
    }
  | {
      name: 'oauthProvidersQuery';
      variables: OauthProvidersQueryVariables;
      response: OauthProvidersQuery;
    }
  | {
      name: 'getPublicUserByIdQuery';
      variables: GetPublicUserByIdQueryVariables;
      response: GetPublicUserByIdQuery;
    }
  | {
      name: 'getUserFeaturesQuery';
      variables: GetUserFeaturesQueryVariables;
      response: GetUserFeaturesQuery;
    }
  | {
      name: 'getWorkspaceAccessQuery';
      variables: GetWorkspaceAccessQueryVariables;
      response: GetWorkspaceAccessQuery;
    }
  | {
      name: 'getWorkspacePageMetaByIdQuery';
      variables: GetWorkspacePageMetaByIdQueryVariables;
      response: GetWorkspacePageMetaByIdQuery;
    }
  | {
      name: 'getWorkspacePublicPagesQuery';
      variables: GetWorkspacePublicPagesQueryVariables;
      response: GetWorkspacePublicPagesQuery;
    }
  | {
      name: 'getWorkspaceSubscriptionQuery';
      variables: GetWorkspaceSubscriptionQueryVariables;
      response: GetWorkspaceSubscriptionQuery;
    }
  | {
      name: 'getWorkspacesQuery';
      variables: GetWorkspacesQueryVariables;
      response: GetWorkspacesQuery;
    }
  | {
      name: 'listHistoryQuery';
      variables: ListHistoryQueryVariables;
      response: ListHistoryQuery;
    }
  | {
      name: 'indexerAggregateQuery';
      variables: IndexerAggregateQueryVariables;
      response: IndexerAggregateQuery;
    }
  | {
      name: 'indexerSearchQuery';
      variables: IndexerSearchQueryVariables;
      response: IndexerSearchQuery;
    }
  | {
      name: 'invoicesQuery';
      variables: InvoicesQueryVariables;
      response: InvoicesQuery;
    }
  | {
      name: 'listNotificationsQuery';
      variables: ListNotificationsQueryVariables;
      response: ListNotificationsQuery;
    }
  | {
      name: 'pricesQuery';
      variables: PricesQueryVariables;
      response: PricesQuery;
    }
  | {
      name: 'serverConfigQuery';
      variables: ServerConfigQueryVariables;
      response: ServerConfigQuery;
    }
  | {
      name: 'subscriptionQuery';
      variables: SubscriptionQueryVariables;
      response: SubscriptionQuery;
    }
  | {
      name: 'workspaceBlobQuotaQuery';
      variables: WorkspaceBlobQuotaQueryVariables;
      response: WorkspaceBlobQuotaQuery;
    }
  | {
      name: 'workspaceInvoicesQuery';
      variables: WorkspaceInvoicesQueryVariables;
      response: WorkspaceInvoicesQuery;
    };

export type Mutations =
  | {
      name: 'generateUserAccessTokenMutation';
      variables: GenerateUserAccessTokenMutationVariables;
      response: GenerateUserAccessTokenMutation;
    }
  | {
      name: 'revokeUserAccessTokenMutation';
      variables: RevokeUserAccessTokenMutationVariables;
      response: RevokeUserAccessTokenMutation;
    }
  | {
      name: 'adminUpdateWorkspaceMutation';
      variables: AdminUpdateWorkspaceMutationVariables;
      response: AdminUpdateWorkspaceMutation;
    }
  | {
      name: 'createChangePasswordUrlMutation';
      variables: CreateChangePasswordUrlMutationVariables;
      response: CreateChangePasswordUrlMutation;
    }
  | {
      name: 'createUserMutation';
      variables: CreateUserMutationVariables;
      response: CreateUserMutation;
    }
  | {
      name: 'deleteAuthSigningKeyMutation';
      variables: DeleteAuthSigningKeyMutationVariables;
      response: DeleteAuthSigningKeyMutation;
    }
  | {
      name: 'deleteUserMutation';
      variables: DeleteUserMutationVariables;
      response: DeleteUserMutation;
    }
  | {
      name: 'disableUserMutation';
      variables: DisableUserMutationVariables;
      response: DisableUserMutation;
    }
  | {
      name: 'enableUserMutation';
      variables: EnableUserMutationVariables;
      response: EnableUserMutation;
    }
  | {
      name: 'importUsersMutation';
      variables: ImportUsersMutationVariables;
      response: ImportUsersMutation;
    }
  | {
      name: 'rotateAuthSigningKeyMutation';
      variables: RotateAuthSigningKeyMutationVariables;
      response: RotateAuthSigningKeyMutation;
    }
  | {
      name: 'sendTestEmailMutation';
      variables: SendTestEmailMutationVariables;
      response: SendTestEmailMutation;
    }
  | {
      name: 'updateAccountFeaturesMutation';
      variables: UpdateAccountFeaturesMutationVariables;
      response: UpdateAccountFeaturesMutation;
    }
  | {
      name: 'updateAccountMutation';
      variables: UpdateAccountMutationVariables;
      response: UpdateAccountMutation;
    }
  | {
      name: 'updateAppConfigMutation';
      variables: UpdateAppConfigMutationVariables;
      response: UpdateAppConfigMutation;
    }
  | {
      name: 'deleteBlobMutation';
      variables: DeleteBlobMutationVariables;
      response: DeleteBlobMutation;
    }
  | {
      name: 'releaseDeletedBlobsMutation';
      variables: ReleaseDeletedBlobsMutationVariables;
      response: ReleaseDeletedBlobsMutation;
    }
  | {
      name: 'setBlobMutation';
      variables: SetBlobMutationVariables;
      response: SetBlobMutation;
    }
  | {
      name: 'abortBlobUploadMutation';
      variables: AbortBlobUploadMutationVariables;
      response: AbortBlobUploadMutation;
    }
  | {
      name: 'completeBlobUploadMutation';
      variables: CompleteBlobUploadMutationVariables;
      response: CompleteBlobUploadMutation;
    }
  | {
      name: 'createBlobUploadMutation';
      variables: CreateBlobUploadMutationVariables;
      response: CreateBlobUploadMutation;
    }
  | {
      name: 'cancelSubscriptionMutation';
      variables: CancelSubscriptionMutationVariables;
      response: CancelSubscriptionMutation;
    }
  | {
      name: 'changeEmailMutation';
      variables: ChangeEmailMutationVariables;
      response: ChangeEmailMutation;
    }
  | {
      name: 'changePasswordMutation';
      variables: ChangePasswordMutationVariables;
      response: ChangePasswordMutation;
    }
  | {
      name: 'createCheckoutSessionMutation';
      variables: CreateCheckoutSessionMutationVariables;
      response: CreateCheckoutSessionMutation;
    }
  | {
      name: 'createWorkspaceMutation';
      variables: CreateWorkspaceMutationVariables;
      response: CreateWorkspaceMutation;
    }
  | {
      name: 'deleteAccountMutation';
      variables: DeleteAccountMutationVariables;
      response: DeleteAccountMutation;
    }
  | {
      name: 'deleteWorkspaceMutation';
      variables: DeleteWorkspaceMutationVariables;
      response: DeleteWorkspaceMutation;
    }
  | {
      name: 'grantDocUserRolesMutation';
      variables: GrantDocUserRolesMutationVariables;
      response: GrantDocUserRolesMutation;
    }
  | {
      name: 'leaveWorkspaceMutation';
      variables: LeaveWorkspaceMutationVariables;
      response: LeaveWorkspaceMutation;
    }
  | {
      name: 'mentionUserMutation';
      variables: MentionUserMutationVariables;
      response: MentionUserMutation;
    }
  | {
      name: 'publishPageMutation';
      variables: PublishPageMutationVariables;
      response: PublishPageMutation;
    }
  | {
      name: 'readAllNotificationsMutation';
      variables: ReadAllNotificationsMutationVariables;
      response: ReadAllNotificationsMutation;
    }
  | {
      name: 'readNotificationMutation';
      variables: ReadNotificationMutationVariables;
      response: ReadNotificationMutation;
    }
  | {
      name: 'recoverDocMutation';
      variables: RecoverDocMutationVariables;
      response: RecoverDocMutation;
    }
  | {
      name: 'removeAvatarMutation';
      variables: RemoveAvatarMutationVariables;
      response: RemoveAvatarMutation;
    }
  | {
      name: 'resumeSubscriptionMutation';
      variables: ResumeSubscriptionMutationVariables;
      response: ResumeSubscriptionMutation;
    }
  | {
      name: 'revokeDocUserRolesMutation';
      variables: RevokeDocUserRolesMutationVariables;
      response: RevokeDocUserRolesMutation;
    }
  | {
      name: 'revokeMemberPermissionMutation';
      variables: RevokeMemberPermissionMutationVariables;
      response: RevokeMemberPermissionMutation;
    }
  | {
      name: 'revokePublicPageMutation';
      variables: RevokePublicPageMutationVariables;
      response: RevokePublicPageMutation;
    }
  | {
      name: 'sendChangeEmailMutation';
      variables: SendChangeEmailMutationVariables;
      response: SendChangeEmailMutation;
    }
  | {
      name: 'sendChangePasswordEmailMutation';
      variables: SendChangePasswordEmailMutationVariables;
      response: SendChangePasswordEmailMutation;
    }
  | {
      name: 'sendSetPasswordEmailMutation';
      variables: SendSetPasswordEmailMutationVariables;
      response: SendSetPasswordEmailMutation;
    }
  | {
      name: 'sendVerifyChangeEmailMutation';
      variables: SendVerifyChangeEmailMutationVariables;
      response: SendVerifyChangeEmailMutation;
    }
  | {
      name: 'sendVerifyEmailMutation';
      variables: SendVerifyEmailMutationVariables;
      response: SendVerifyEmailMutation;
    }
  | {
      name: 'updateDocDefaultRoleMutation';
      variables: UpdateDocDefaultRoleMutationVariables;
      response: UpdateDocDefaultRoleMutation;
    }
  | {
      name: 'updateDocUserRoleMutation';
      variables: UpdateDocUserRoleMutationVariables;
      response: UpdateDocUserRoleMutation;
    }
  | {
      name: 'updateSubscriptionMutation';
      variables: UpdateSubscriptionMutationVariables;
      response: UpdateSubscriptionMutation;
    }
  | {
      name: 'updateUserProfileMutation';
      variables: UpdateUserProfileMutationVariables;
      response: UpdateUserProfileMutation;
    }
  | {
      name: 'uploadAvatarMutation';
      variables: UploadAvatarMutationVariables;
      response: UploadAvatarMutation;
    }
  | {
      name: 'verifyEmailMutation';
      variables: VerifyEmailMutationVariables;
      response: VerifyEmailMutation;
    }
  | {
      name: 'setEnableSharingMutation';
      variables: SetEnableSharingMutationVariables;
      response: SetEnableSharingMutation;
    }
  | {
      name: 'inviteByEmailsMutation';
      variables: InviteByEmailsMutationVariables;
      response: InviteByEmailsMutation;
    }
  | {
      name: 'acceptInviteByInviteIdMutation';
      variables: AcceptInviteByInviteIdMutationVariables;
      response: AcceptInviteByInviteIdMutation;
    }
  | {
      name: 'createInviteLinkMutation';
      variables: CreateInviteLinkMutationVariables;
      response: CreateInviteLinkMutation;
    }
  | {
      name: 'revokeInviteLinkMutation';
      variables: RevokeInviteLinkMutationVariables;
      response: RevokeInviteLinkMutation;
    }
  | {
      name: 'approveWorkspaceTeamMemberMutation';
      variables: ApproveWorkspaceTeamMemberMutationVariables;
      response: ApproveWorkspaceTeamMemberMutation;
    }
  | {
      name: 'grantWorkspaceTeamMemberMutation';
      variables: GrantWorkspaceTeamMemberMutationVariables;
      response: GrantWorkspaceTeamMemberMutation;
    };
